import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { latestUpdates } from '../../constants/updates';
import { Button } from '../../components/ui/button';

export const UpdateDetailsPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const id = Number(params.id);

    const update = useMemo(() => latestUpdates.find((u) => u.id === id), [id]);

    if (!update) {
        return (
            <div className="p-6">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h1 className="text-lg font-semibold mb-2">Not found</h1>
                        <p className="text-sm text-muted-foreground mb-4">The requested update does not exist.</p>
                        <Button variant="outline" onClick={ () => navigate(-1) }>Go back</Button>
                    </div>
                </div>
            </div>
        );
    }

    // Special rich content for the first update (use full text from screenshot)
    if (update.id === 1) {
        return (
            <div className="p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            {/* Text */ }
                            <div className="p-6 lg:p-10 flex flex-col gap-6">
                                <div>
                                    <h1 className="text-4xl font-extrabold tracking-tight text-card-foreground mb-2">Copilot Beta Launch!</h1>
                                    <p className="text-base text-muted-foreground">Be among the first to try our new Delphos Copilot platform and start writing music faster than you ever thought possible.</p>
                                </div>

                                <div className="space-y-4 text-card-foreground text-sm leading-relaxed">
                                    <p>This is the dawn of a new workflow for composers, producers and editors. We’re excited to announce the public beta release of Delphos Copilot. A musical intelligence that will help you write faster, think of ideas and get to a finished track in a fraction of the time.</p>

                                    <div>
                                        <p className="font-semibold mb-2">In the beta you can</p>
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>Generate short instrumentals and ideas from a range of styles</li>
                                            <li>Use the new Fast Track features - give the Copilot an audio clip and it will compose new instrumental tracks that go with it</li>
                                            <li>Export as audio, midi or full instrumental stems.</li>
                                        </ul>
                                    </div>

                                    <p>The UI is quite plain at the moment. We’ve stripped away any distractions so that our earliest public users can see how the generators fit into their workflow. We highly welcome all feedback, good and bad.</p>

                                    <p>The goal is to automate away all the bits of music writing that you don’t like to do - so you can just focus on the bits you love.</p>

                                    <div className="space-y-1">
                                        <p className="font-semibold">You can now earn credits for feedback! Please get in touch with our support team and we’ll take it from there.</p>
                                    </div>
                                </div>

                                <div className="mt-2 flex items-center gap-3">
                                    <Button variant="default" onClick={ () => navigate('/explore') }>Try it now</Button>
                                    <Link to="/home" className="text-sm underline text-primary">Back to Home</Link>
                                </div>
                            </div>
                            {/* Image */ }
                            <div className="w-full h-full">
                                <img src={ update.image } alt={ update.title } className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Special rich content for the second update (Fast Track)
    if (update.id === 2) {
        return (
            <div className="p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            {/* Text */ }
                            <div className="p-6 lg:p-10 flex flex-col gap-6">
                                <div>
                                    <h1 className="text-4xl font-extrabold tracking-tight text-card-foreground mb-2">Launching: Fast Track</h1>
                                    <p className="text-base text-muted-foreground">Turn any loop, hook or sketch into new full instrumentals in minutes.</p>
                                </div>

                                <div className="space-y-4 text-card-foreground text-sm leading-relaxed">
                                    <p>Getting to the winning idea for a composition is often the hardest part. But what comes after determines whether or not a song is finished and gets released.</p>
                                    <p>You have to write the drums, the supporting lines. It can really slow down the process and distract you from hearing the song as a whole.</p>
                                    <p>That’s why we built <span className="font-semibold">Fast Track</span>.</p>
                                    <p>Upload some audio - a central hook or loop you want to use, or it can be an inspiration track. The Copilot will listen to it and grow <span className="font-semibold">additional instrumental tracks</span> for it. What’s more - you can use any soundworld for this!</p>
                                    <p>Fast Track is quite possibly our most powerful and popular instrument to date, being able to shave off countless hours in repetitive writing workflow.</p>
                                    <div className="space-y-1">
                                        <p className="font-semibold">We’re looking forward to hearing what you write with it!</p>
                                    </div>
                                </div>

                                <div className="mt-2 flex items-center gap-3">
                                    <Button variant="default" onClick={ () => navigate('/catalogue') }>Try it now</Button>
                                    <Link to="/home" className="text-sm underline text-primary">Back to Home</Link>
                                </div>
                            </div>
                            {/* Image */ }
                            <div className="w-full h-full">
                                <img src={ update.image } alt={ update.title } className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Special rich content for the third update (Soundworld Library)
    if (update.id === 3) {
        return (
            <div className="p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            {/* Text */ }
                            <div className="p-6 lg:p-10 flex flex-col gap-6">
                                <div>
                                    <h1 className="text-4xl font-extrabold tracking-tight text-card-foreground mb-2">Soundworld Library</h1>
                                    <p className="text-base text-muted-foreground">Introducing new single instrument soundworlds!</p>
                                </div>

                                <div className="space-y-4 text-card-foreground text-sm leading-relaxed">
                                    <p>To celebrate the Copilot Beta, we have updated our roster of soundworlds. They are built on the very latest state-of-the-art Delphos models. More controlled composition, more variety, better sound.</p>
                                    <p>The <span className="font-semibold">classic full soundworlds</span> are all there, which you can use to create multiple layers of composition at the same time. Drums, bass, chords, arps, lead.</p>
                                    <p>But there’s also a new type of soundworld.</p>
                                    <p><span className="font-semibold">Single instrument soundworlds</span> provide textures and individual layers for your tracks that draw on a particular instrument.</p>
                                    <p>Add a guitar to any track, or some light strings. Or focus just on the drums.</p>
                                    <p className="font-semibold">We’ll be adding more soundworlds as usual. And we do take requests!</p>
                                </div>

                                <div className="mt-2 flex items-center gap-3">
                                    <Button variant="default" onClick={ () => navigate('/catalogue') }>Try it now</Button>
                                    <Link to="/home" className="text-sm underline text-primary">Back to Home</Link>
                                </div>
                            </div>
                            {/* Image */ }
                            <div className="w-full h-full">
                                <img src={ update.image } alt={ update.title } className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Special rich content for the fourth update (Audio Utilities)
    if (update.id === 4) {
        return (
            <div className="p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            {/* Text */ }
                            <div className="p-6 lg:p-10 flex flex-col gap-6">
                                <div>
                                    <h1 className="text-4xl font-extrabold tracking-tight text-card-foreground mb-2">Audio Utilities</h1>
                                    <p className="text-base text-muted-foreground">Stem separation, mastering and chord detection</p>
                                </div>

                                <div className="space-y-4 text-card-foreground text-sm leading-relaxed">
                                    <p>To keep essential audio editing functionalities in one place, we’ve now added <span className="font-semibold">stem separation</span>, <span className="font-semibold">auto mastering</span> and <span className="font-semibold">chord detection</span> to the Copilot.</p>
                                    <p>Stem separation comes in two forms currently: instrument isolation and drum part isolation for when you want to grab just kick or snare.</p>
                                    <p>You may find all of these under the <span className="font-semibold">Audio Utilities</span> tab.</p>
                                </div>

                                <div className="mt-2 flex items-center gap-3">
                                    <Button variant="default" onClick={ () => navigate('/audio-utilities') }>Try it now</Button>
                                    <Link to="/home" className="text-sm underline text-primary">Back to Home</Link>
                                </div>
                            </div>
                            {/* Image */ }
                            <div className="w-full h-full">
                                <img src={ update.image } alt={ update.title } className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="max-w-6xl mx-auto">
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        {/* Text */ }
                        <div className="p-6 lg:p-10 flex flex-col gap-4">
                            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-card-foreground">{ update.title }</h1>
                            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{ update.longDescription || update.description }</p>
                            <div className="mt-2 flex items-center gap-3">
                                <Button variant="default" onClick={ () => navigate('/explore') }>Try it now</Button>
                                <Link to="/home" className="text-sm underline text-primary">Back to Home</Link>
                            </div>
                        </div>
                        {/* Image */ }
                        <div className="w-full h-full">
                            <img src={ update.image } alt={ update.title } className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateDetailsPage;


